/**
 * Workspace chrome state — the bridge between a page and the persistent shell.
 *
 * The Topbar lives in the `(workspace)` layout route and mounts once for
 * the whole session (so navigating between pages never tears the chrome
 * down — that full remount was the old navigation flicker). But breadcrumbs
 * and the bell count are still declared per-page via `<WorkspaceShell
 * breadcrumbs={…}>`. This store carries those per-page values *up* to the
 * persistent Topbar: `WorkspaceShell` publishes them on mount, the layout
 * subscribes and renders them.
 *
 * Kept beside the chrome components (same `zustand` idiom) so the page-level
 * shell and the layout-level chrome can share state without importing each
 * other (anti-pattern: The Sibling Importer).
 */
import { create } from "zustand";
import type { LucideIcon } from "lucide-react";
import type { BreadcrumbItem } from "../components/breadcrumbs.tsx";

/**
 * A page-owned section tab the Topbar renders in place of the nav-config
 * group tabs. Pages whose "sections" are filters rather than routes (e.g.
 * the Inbox's Needs approval / Approved / Rejected) publish these so the
 * tabs live in the chrome like every other module's.
 */
export interface ChromeSectionTab {
  label: string;
  icon: LucideIcon;
  /** Router path the tab links to. */
  to: string;
  /** Optional search params carried on the link (filter tabs). */
  search?: Record<string, string>;
  /** The publisher decides activeness (pathname alone can't — same route). */
  active: boolean;
  /** Optional attention count, rendered as the red pill (e.g. pending approvals). */
  count?: number;
}

export interface WorkspaceChromeState {
  /** Topbar breadcrumb trail for the active page. */
  breadcrumbs: BreadcrumbItem[];
  /** Bell ping count for the active page (0 until a page overrides it). */
  unreadNotifications: number;
  /**
   * When true, the Topbar skips rendering the section tabs for the active
   * page. Routes that own their full viewport (e.g. `/reports/new`) opt in
   * by passing `hideSectionTabs` on `WorkspaceShell`.
   */
  hideSectionTabs: boolean;
  /**
   * When true, the `(workspace)` layout skips the Topbar entirely. For modules
   * that carry their own full chrome — the Store admin renders its own left
   * rail with a brand header and an account footer, so the topbar would be a
   * second, competing navigation surface.
   *
   * `setChrome` replaces state wholesale and every page publishes on mount, so
   * this resets to `false` the moment a page that doesn't opt in renders.
   */
  hideTopbar: boolean;
  /**
   * Page-published tabs that replace the nav-config group tabs in the
   * Topbar. `null` (the default) means "derive tabs from nav-config".
   */
  sectionTabs: ChromeSectionTab[] | null;
  /** Publish the active page's chrome. Called from `WorkspaceShell` on mount. */
  setChrome: (next: {
    breadcrumbs: BreadcrumbItem[];
    unreadNotifications: number;
    hideSectionTabs: boolean;
    hideTopbar: boolean;
    sectionTabs: ChromeSectionTab[] | null;
  }) => void;
}

export const useWorkspaceChrome = create<WorkspaceChromeState>((set) => ({
  breadcrumbs: [{ label: "Workspace" }],
  unreadNotifications: 0,
  hideSectionTabs: false,
  hideTopbar: false,
  sectionTabs: null,
  setChrome: (next) => set(next),
}));
